import { inspectionWarningText } from "@/modules/dataset/public";
import { LabelChip, Spinner, Text } from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import type { DatasetDetails } from "@/api";
import { hatcheryTextModes } from "@/app/theme";
import type { LoadState } from "@/components/dataset/loadState";
import { noticeChipClassName } from "@/shared/ui/emptyState/public";
import {
  inlineLoadingLineClassName,
  inlineSpinnerClassName,
} from "@/shared/ui/loadingState/public";
import styles from "./DatasetReviewPanel.module.css";

type InspectResult = DatasetDetails["inspect_result"];
type InspectWarning = NonNullable<InspectResult["warnings"]>[number];
const STATUS_COLORS = { present: 0x16a34a, reconstructed: 0x2563eb, unknown: 0x64748b } as const;
type InspectionStatus = keyof typeof STATUS_COLORS;

/** 검사 상태는 한 줄, 조치가 필요한 경고만 본문에 남긴다. FPS는 영상 영역이 소유한다. */
export function MetadataCard({
  state,
  videoFpsTarget,
}: {
  state: LoadState<DatasetDetails>;
  videoFpsTarget?: string;
}): ReactNode {
  const inspect = state.status === "success" ? state.data.inspect_result : null;
  const status = inspectionStatus(inspect?.meta?.status);
  const warnings = inspect ? advisories(inspect) : [];
  return (
    <section className={styles.inspection} aria-label="메타데이터">
      <div className={styles.inspectionSummary}>
        <Text variant={hatcheryTextModes["caption-bold"]}>메타데이터</Text>
        {inspect ? (
          <>
            <LabelChip
              color={STATUS_COLORS[status]}
              size="sm"
              variant="filled"
              title="meta/info.json의 검사 상태"
            >
              {formatStatus(status)}
            </LabelChip>
            <span className={styles.muted}>
              {warnings.length > 0 ? `경고 ${warnings.length}건` : "경고 없음"}
            </span>
            {inspect.hatchery_data_version?.trim() ? (
              <details className={styles.inspectionVersion}>
                <summary>검사 정보</summary>
                <span>hatchery-data {inspect.hatchery_data_version}</span>
              </details>
            ) : null}
          </>
        ) : state.status === "error" ? (
          <span className={noticeChipClassName}>메타데이터 검사 결과를 불러오지 못했습니다.</span>
        ) : (
          <span className={`${styles.muted} ${inlineLoadingLineClassName}`}>
            <Spinner size="sm" className={inlineSpinnerClassName} />
            메타데이터 검사 결과를 불러오고 있습니다.
          </span>
        )}
        {videoFpsTarget ? (
          <a href={`#${videoFpsTarget}`} className={styles.warn}>
            FPS 미확정 · 영상 정보 확인
          </a>
        ) : null}
      </div>
      {warnings.length > 0 ? (
        <ul className={styles.inspectionWarnings}>
          {warnings.map((warning, index) => (
            <li key={warning.code + ":" + index} className={styles.inspectionWarningItem}>
              <span>{inspectionWarningText(warning.code) ?? warning.code}</span>
              <span className={styles.inspectionWarningSeverity}>
                {warning.severity === "warning" ? "주의" : "안내"}
                {warning.total_count !== undefined
                  ? ` · 총 ${warning.total_count.toLocaleString()}건`
                  : ""}
              </span>
              <details>
                <summary>검사 상세 보기</summary>
                <code>{warning.code}</code>
                {warning.detail ? <p>{warning.detail}</p> : null}
              </details>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

// 상태 칩이 이미 표시하는 내용을 문장으로 반복하는 경고는 목록에서 뺀다. backend 는
// `meta_reconstructed` 를 항상 `status: "reconstructed"` 와 함께 보내고, detail 에도 같은
// 사실만 담는다.
const STATUS_ECHO_CODES: readonly string[] = ["meta_reconstructed"];

function advisories(inspect: InspectResult): readonly InspectWarning[] {
  return (inspect.warnings ?? []).filter((warning) => !STATUS_ECHO_CODES.includes(warning.code));
}

// 서버가 보내는 값은 `present`·`reconstructed` 둘이다. 값이 없거나 그 밖의 값이면 판정이 없는
// 상태로 표시한다.
function inspectionStatus(status: string | undefined): InspectionStatus {
  return status === "present" || status === "reconstructed" ? status : "unknown";
}

// 상태 칩 문구. 판단 근거만 짧게 표시한다. 무엇의 상태인지는 카드 제목이 이미 표시한다.
function formatStatus(status: InspectionStatus): string {
  switch (status) {
    case "present":
      return "원본";
    case "reconstructed":
      return "생성됨";
    case "unknown":
      return "확인 안 됨";
  }
}
